import {z} from "zod/v4-mini"
import type {Evaluator} from "@cm/graph-v3"
import {registerTemplateGraphNode, skipped, getTemplateNodeReturns, numberLike, TypeDescriptors, visitNone} from "@cm/template-nodes"
import type {
    OnFilterActiveContext,
    OnCompileContext,
    TemplateNodeContext,
    TemplateNodeDefinition,
    VisitorNodeResult,
    NumberLike,
    IGetInfoNodeParts,
    BuilderOutlet,
    GraphBuilderScope,
    NodeEvaluator,
} from "@cm/template-nodes"
import {MattressNode} from "#mattress/nodes/mattress-node"
import {mattressReferenceFrameLikeMarker} from "#mattress/role-markers"
import {mattressReferenceFrameLike, mattressGlobalReferenceFrame, mattressReferenceFrameType} from "#mattress/validation"
import type {MattressReferenceFrameLikeNode} from "#mattress/role-markers"
import type {MattressReferenceFrameType} from "#mattress/validation"
import type {MattressNodePath} from "#mattress/nodes/component/mattress-component-types"
import {
    aliasReferenceMattressFrameData,
    mattressReferenceFramesDataListsPush,
    resolveMattressReferenceFrameData,
    mattressReferenceFrameToGlobalUVSpace,
    mattressReferenceFrameFromGlobalUVSpace,
    getMattressCompileContext,
} from "#mattress/nodes/component/mattress-component-helpers"
import {
    fromParentCanonicalUVToReferenceFrameUV,
    toParentCanonicalUVFromReferenceFrameUV,
    toParentReferenceFrameUVFromReferenceFrameUV,
    fromParentReferenceFrameUVToReferenceFrameUV,
} from "#mattress/nodes/reference-frame/mattress-reference-frame-math"
import {hashObject} from "@cm/utils"

import {type Vector2Like} from "@cm/math"

const TD = TypeDescriptors

const mattressNodePathArrayDesc = TD.Array(TD.Identity<MattressNodePath[number]>())

//////////////////////////////////////////////////////////////////////////////////////////////////

// Runtime-evaluated frame produced by `MattressReferenceFrame.evaluate(...)`.
export type MattressReferenceFrameData = {
    contentHash: string
    toGlobalSpace: (uv: Vector2Like) => Vector2Like
    fromGlobalSpace: (uv: Vector2Like) => Vector2Like
    path: MattressNodePath
}

export type MattressGlobalReferenceFrame = "global"

export type MattressReferenceFrameParameters = {
    name: string
    type: MattressReferenceFrameType
    parent: MattressReferenceFrameLikeNode | MattressGlobalReferenceFrame
    origin: NumberLike | null
    unit: NumberLike | null
}

const definition: TemplateNodeDefinition<MattressReferenceFrameParameters> = {
    nodeClass: "MattressReferenceFrame",
    validation: {
        parameters: z.object({
            name: z.string(),
            type: mattressReferenceFrameType,
            parent: z.union([mattressReferenceFrameLike, mattressGlobalReferenceFrame]),
            origin: z.nullable(numberLike),
            unit: z.nullable(numberLike),
        }),
        returns: getTemplateNodeReturns(),
    },
}

@registerTemplateGraphNode
export class MattressReferenceFrame extends MattressNode<MattressReferenceFrameParameters> implements MattressReferenceFrameLikeNode {
    get [mattressReferenceFrameLikeMarker]() {
        return true as const
    }

    public override get definition() {
        return definition
    }

    public override onFilterActive(
        _context: OnFilterActiveContext,
        _evaluator: Evaluator<TemplateNodeContext>,
    ): VisitorNodeResult<MattressReferenceFrameParameters> {
        if (this.parameters.origin === null || this.parameters.unit === null) return skipped
        return visitNone(this.parameters)
    }

    public override onCompile(context: OnCompileContext, _evaluator: Evaluator<TemplateNodeContext>): VisitorNodeResult<MattressReferenceFrameParameters> {
        const mattressContext = getMattressCompileContext(context)
        const {evaluator} = context
        const {evaluatedData, nodePath, evaluatePartialMattressMeshData} = mattressContext
        const {referenceFramesDataLists} = evaluatedData
        const {parent, type, origin, unit} = this.parameters

        const scope = evaluator.getScope(this)

        const parentData = parent === "global" ? ("global" as const) : evaluator.evaluate(parent)
        const originData = origin !== null ? evaluator.evaluate(origin) : null
        const unitData = unit !== null ? evaluator.evaluate(unit) : null

        const [mattressMeshDataValid] = scope.branch(evaluatePartialMattressMeshData(scope, evaluator))
        const [baseMeshGeometryParametersValid] = scope.branch(scope.get(mattressMeshDataValid, "geometryParameters"))

        const newNodePath = scope.pureLambda(scope.tuple(nodePath, this), ([nodePath, instance]) => [...nodePath, instance], "newPath", {
            output: mattressNodePathArrayDesc,
        })

        // TODO refine output TD
        const referenceFrameData = scope.pureLambda(
            scope.tuple(parentData, originData, unitData, type, baseMeshGeometryParametersValid, newNodePath),
            ([evaluatedParent, evaluatedOrigin, evaluatedUnit, type, baseMeshGeometryParameters, newNodePath]) => {
                if (evaluatedParent === null || evaluatedOrigin === null || evaluatedUnit === null || baseMeshGeometryParameters === null) return null
                const toGlobalSpace = (uv: Vector2Like) =>
                    evaluatedParent === "global"
                        ? toParentCanonicalUVFromReferenceFrameUV(uv, type, evaluatedOrigin, evaluatedUnit, baseMeshGeometryParameters)
                        : evaluatedParent.toGlobalSpace(toParentReferenceFrameUVFromReferenceFrameUV(uv, type, type, evaluatedOrigin, evaluatedUnit))
                const fromGlobalSpace = (uv: Vector2Like) =>
                    evaluatedParent === "global"
                        ? fromParentCanonicalUVToReferenceFrameUV(uv, type, evaluatedOrigin, evaluatedUnit, baseMeshGeometryParameters)
                        : fromParentReferenceFrameUVToReferenceFrameUV(evaluatedParent.fromGlobalSpace(uv), type, type, evaluatedOrigin, evaluatedUnit)
                return {
                    contentHash: hashObject({
                        type: "MattressReferenceFrameData",
                        parent: evaluatedParent === "global" ? "global" : evaluatedParent.contentHash,
                        referenceFrameType: type,
                        origin: evaluatedOrigin,
                        unit: evaluatedUnit,
                    }),
                    toGlobalSpace,
                    fromGlobalSpace,
                    path: newNodePath,
                }
            },
            "mattressReferenceFrameData",
        )

        mattressReferenceFramesDataListsPush(scope, referenceFramesDataLists, referenceFrameData)
        aliasReferenceMattressFrameData(this, evaluator, referenceFrameData)
        return visitNone(this.parameters)
    }

    toGlobalUVSpace(uv: Vector2Like, context: IGetInfoNodeParts): Vector2Like {
        return mattressReferenceFrameToGlobalUVSpace(this, uv, context)
    }

    fromGlobalUVSpace(uv: Vector2Like, context: IGetInfoNodeParts): Vector2Like {
        return mattressReferenceFrameFromGlobalUVSpace(this, uv, context)
    }

    evaluate(scope: GraphBuilderScope, evaluator: NodeEvaluator): BuilderOutlet<MattressReferenceFrameData | null> {
        return resolveMattressReferenceFrameData(this, scope, evaluator)
    }
}

export {mattressGlobalReferenceFrame}
